import {
  chartsTooltipClasses,
  ChartsTooltipContainer,
  type ChartsTooltipProps,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import dayjs from 'dayjs';
import { type ReactNode } from 'react';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import { Inline, Text } from '@/ui';
import { Box, type Theme, useTheme } from '@/ui/primitives';
import { getChartTooltipSx } from '@/ui/primitives/chartTooltipStyles';
import { MuiSparkLineChart } from '@/ui/primitives/xCharts';
import { cloneMetricTypography } from './testsColumns.cloneMetricTypography';
import { CloneCellContainer } from './testsColumns.cloneSharedCells';

type DeltaArrowMode = 'standard' | 'duration';

type CloneMetricColor = string | ((muiTheme: Theme) => string);

type CloneMetricSparkCellProps = {
  valueLabel: string;
  hasValue: boolean;
  deltaLabel: string | null;
  deltaIsGood: boolean | null;
  deltaColor: CloneMetricColor;
  deltaArrowMode?: DeltaArrowMode;
  yAxisMin?: number;
  yAxisMax?: number;
  sparklineData: number[];
  sparklineDates: Date[];
  sparkStroke: CloneMetricColor;
  sparklineValueFormatter: (value: number | null) => string;
  resolveTooltipSwatchColor: (theme: Theme, value: number | null) => string;
};

type CloneSparklineTooltipProps = ChartsTooltipProps & {
  resolveTooltipSwatchColor: (theme: Theme, value: number | null) => string;
};

const resolveChartTooltipBaseSx = (theme: Theme): Record<string, unknown> => {
  const base = getChartTooltipSx(theme);
  if (!base || Array.isArray(base) || typeof base === 'function') return {};
  return base as Record<string, unknown>;
};

const renderDeltaWithArrow = (
  label: string,
  isGood: boolean,
  arrowMode: DeltaArrowMode,
): ReactNode =>
  label === 'No change' ? (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: `${cloneMetricTypography.deltaFontSize} !important`,
        lineHeight: `${cloneMetricTypography.deltaLineHeight} !important`,
      }}
    >
      {label}
    </Box>
  ) : (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        fontSize: `${cloneMetricTypography.deltaFontSize} !important`,
        lineHeight: `${cloneMetricTypography.deltaLineHeight} !important`,
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: `${cloneMetricTypography.deltaFontSize} !important`,
          lineHeight: `${cloneMetricTypography.deltaLineHeight} !important`,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: cloneMetricTypography.arrowFontSize,
          lineHeight: 1,
          color: 'currentColor',
          opacity: (theme) => (isLightTheme(theme) ? 0.56 : 0.72),
        }}
      >
        {arrowMode === 'duration' ? (isGood ? '▲' : '▼') : isGood ? '▲' : '▼'}
      </Box>
    </Box>
  );

const CloneSparklineTooltip = ({
  classes,
  sx,
  resolveTooltipSwatchColor,
  ...props
}: CloneSparklineTooltipProps): JSX.Element | null => {
  const tooltipData = useAxesTooltip();
  const theme = useTheme();
  if (!tooltipData || tooltipData.length === 0) return null;

  const paperClassName = `${chartsTooltipClasses.paper} ${classes?.paper ?? ''}`.trim();
  const tableClassName = `${chartsTooltipClasses.table} ${classes?.table ?? ''}`.trim();
  const rowClassName = chartsTooltipClasses.row;
  const baseCellClassName = `${chartsTooltipClasses.cell} ${classes?.cell ?? ''}`.trim();
  const valueCellClassName = `${baseCellClassName} ${chartsTooltipClasses.valueCell}`.trim();
  const markCellClassName = `${baseCellClassName} MuiChartsTooltip-markCell`.trim();
  const markContainerClassName = chartsTooltipClasses.markContainer.trim();
  const markClassName = `${chartsTooltipClasses.mark} ${classes?.mark ?? ''}`.trim();

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <div className={paperClassName}>
        {tooltipData.map(({ axisId, axisFormattedValue, seriesItems }) => {
          const pointItem = seriesItems[0];
          const parsedValue =
            typeof pointItem.value === 'number' ? pointItem.value : Number(pointItem.value);
          const pointValue = Number.isFinite(parsedValue) ? parsedValue : null;
          const displayValue = pointValue === null ? '-' : pointItem.formattedValue;
          const swatchColor = resolveTooltipSwatchColor(theme, pointValue);

          return (
            <table key={String(axisId)} className={`${tableClassName} flowqa-spark-tooltip-table`}>
              <caption>{axisFormattedValue}</caption>
              <tbody>
                <tr className={rowClassName}>
                  <th className={markCellClassName}>
                    <div className={markContainerClassName}>
                      <Box
                        component="span"
                        className={markClassName}
                        sx={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: r('sm'),
                          backgroundColor: swatchColor,
                        }}
                      />
                    </div>
                  </th>
                  <td className={valueCellClassName}>{displayValue}</td>
                </tr>
              </tbody>
            </table>
          );
        })}
      </div>
    </ChartsTooltipContainer>
  );
};

export const CloneMetricSparkCell = ({
  valueLabel,
  hasValue,
  deltaLabel,
  deltaIsGood,
  deltaColor,
  deltaArrowMode = 'standard',
  yAxisMin,
  yAxisMax,
  sparklineData,
  sparklineDates,
  sparkStroke,
  sparklineValueFormatter,
  resolveTooltipSwatchColor,
}: CloneMetricSparkCellProps): JSX.Element => {
  const sparklineMin = sparklineData.length > 0 ? Math.min(...sparklineData) : 0;
  const sparklineMax = sparklineData.length > 0 ? Math.max(...sparklineData) : 0;
  const isZeroLine = sparklineData.length > 0 && sparklineData.every((value) => value === 0);
  const sparklineRange = sparklineMax - sparklineMin;
  const sparklinePadding =
    sparklineRange > 0 ? sparklineRange * 0.2 : Math.max(Math.abs(sparklineMax) * 0.08, 1);
  const baseYAxisMin = yAxisMin ?? 0;
  const computedYAxisMin =
    yAxisMin !== undefined
      ? baseYAxisMin
      : sparklineData.length > 0
        ? Math.min(baseYAxisMin, sparklineMin - sparklinePadding)
        : 0;
  const computedYAxisMax = Math.max(sparklineMax + sparklinePadding, computedYAxisMin + 1);
  const resolvedYAxisMaxBase =
    yAxisMax !== undefined
      ? Math.max(yAxisMax, sparklineMax, computedYAxisMin + 1)
      : computedYAxisMax;
  const noDataBaseline = !hasValue && isZeroLine;
  const resolvedYAxisMin = noDataBaseline ? -0.05 : computedYAxisMin;
  const resolvedYAxisMax = noDataBaseline ? 1 : resolvedYAxisMaxBase;
  const highlightRadius = 3.2;
  const horizontalViewportPadding = highlightRadius + 1;
  const clipTop = noDataBaseline ? 0 : resolvedYAxisMax <= 100 ? 4 : 1;
  const clipBottom = 1;
  const sparklineMarginBottom = 1;
  const resolvedChartHeight = 34;
  const hasRenderableSparkline =
    sparklineData.length > 0 && sparklineDates.length === sparklineData.length;
  const shouldRenderSparkline = hasRenderableSparkline;
  const resolveSparkStroke = (theme: Theme): string => {
    if (typeof sparkStroke === 'function') {
      return sparkStroke(theme);
    }
    if (sparkStroke === 'text.secondary') {
      return theme.app.colors.textMuted;
    }
    if (sparkStroke === 'text.primary') {
      const textPrimary: string = theme.app.colors.text;
      return textPrimary;
    }
    return sparkStroke;
  };

  return (
    <CloneCellContainer gap="none">
      <Inline
        gap="sm"
        align="center"
        wrap="nowrap"
        sx={{ width: '100%', minWidth: 0, minHeight: cloneMetricTypography.valueLineHeight }}
      >
        <Text
          role="body"
          sx={{
            color: hasValue ? 'text.primary' : 'text.secondary',
            fontSize: `${cloneMetricTypography.valueFontSize} !important`,
            lineHeight: `${cloneMetricTypography.valueLineHeight} !important`,
          }}
        >
          {valueLabel}
        </Text>
        {deltaLabel ? (
          <Text
            role="body"
            sx={{
              fontSize: `${cloneMetricTypography.deltaFontSize} !important`,
              lineHeight: `${cloneMetricTypography.deltaLineHeight} !important`,
              color: deltaColor,
            }}
          >
            {renderDeltaWithArrow(deltaLabel, deltaIsGood ?? false, deltaArrowMode)}
          </Text>
        ) : null}
      </Inline>
      <Box
        sx={{
          flex: '0 0 auto',
          width: '100%',
          minWidth: '96px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          pt: s('none'),
          pb: s('none'),
        }}
      >
        <Box sx={{ height: `${resolvedChartHeight}px`, width: '100%', position: 'relative' }}>
          {shouldRenderSparkline ? (
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <MuiSparkLineChart
                color="currentColor"
                data={sparklineData}
                xAxis={{
                  scaleType: 'time',
                  data: sparklineDates,
                  min: sparklineDates[0],
                  max: sparklineDates[sparklineDates.length - 1],
                  valueFormatter: (value: Date | string | number): string =>
                    dayjs(value).format('DD/MM/YYYY'),
                }}
                yAxis={{ min: resolvedYAxisMin, max: resolvedYAxisMax, scaleType: 'linear' }}
                margin={{
                  top: 5,
                  bottom: sparklineMarginBottom,
                  left: horizontalViewportPadding,
                  right: horizontalViewportPadding,
                }}
                valueFormatter={sparklineValueFormatter}
                height={resolvedChartHeight}
                curve="monotoneX"
                clipAreaOffset={{
                  top: Math.max(clipTop, highlightRadius),
                  right: highlightRadius,
                  bottom: clipBottom,
                  left: highlightRadius,
                }}
                showTooltip
                showHighlight
                slots={{
                  tooltip: (tooltipProps) => (
                    <CloneSparklineTooltip
                      {...tooltipProps}
                      resolveTooltipSwatchColor={resolveTooltipSwatchColor}
                    />
                  ),
                }}
                slotProps={{
                  tooltip: {
                    sx: (theme) => {
                      const tooltipFontSize = theme.typography.body2.fontSize ?? '13px';
                      const tooltipLineHeight = theme.typography.body2.lineHeight ?? 1.5;
                      return {
                        ...resolveChartTooltipBaseSx(theme),
                        '& .MuiChartsTooltip-paper': {
                          paddingTop: '0 !important',
                          paddingBottom: '0 !important',
                          paddingLeft: '4px !important',
                          paddingRight: '4px !important',
                        },
                        '& .flowqa-spark-tooltip-table > caption': {
                          borderBottom: `1px solid ${theme.app.colors.border} !important`,
                          paddingTop: '6px !important',
                          paddingBottom: '4px !important',
                          paddingLeft: '4px !important',
                          paddingRight: '4px !important',
                          fontSize: tooltipFontSize,
                          textAlign: 'left',
                          lineHeight: tooltipLineHeight,
                        },
                        '& .flowqa-spark-tooltip-table > tbody > tr > th, & .flowqa-spark-tooltip-table > tbody > tr > td':
                          {
                            border: '0 !important',
                            paddingTop: '2px !important',
                            paddingBottom: '2px !important',
                            paddingLeft: '4px !important',
                            paddingRight: '4px !important',
                            fontSize: tooltipFontSize,
                            lineHeight: tooltipLineHeight,
                            verticalAlign: 'middle !important',
                          },
                        '& .flowqa-spark-tooltip-table > tbody > tr:first-of-type > th, & .flowqa-spark-tooltip-table > tbody > tr:first-of-type > td':
                          {
                            paddingTop: '6px !important',
                          },
                        '& .flowqa-spark-tooltip-table > tbody > tr:last-of-type > th, & .flowqa-spark-tooltip-table > tbody > tr:last-of-type > td':
                          {
                            paddingBottom: '4px !important',
                          },
                        '& .flowqa-spark-tooltip-table .MuiChartsTooltip-markCell': {
                          width: '16px !important',
                          minWidth: '16px !important',
                          paddingRight: '2px !important',
                        },
                        '& .flowqa-spark-tooltip-table .MuiChartsTooltip-valueCell': {
                          fontSize: tooltipFontSize,
                          lineHeight: tooltipLineHeight,
                          textAlign: 'right !important',
                          minWidth: '0 !important',
                          whiteSpace: 'nowrap',
                          paddingLeft: '2px !important',
                        },
                      };
                    },
                  },
                  lineHighlight: {
                    r: highlightRadius,
                    stroke: 'none',
                    strokeWidth: 0,
                  },
                }}
                sx={{
                  width: '100%',
                  overflow: 'visible',
                  color: resolveSparkStroke,
                  '& .MuiChartsSurface-root': {
                    overflow: 'visible',
                  },
                  '& svg': {
                    overflow: 'visible',
                  },
                  '& .MuiLineElement-root': {
                    stroke: 'currentColor !important',
                    strokeWidth: '2px !important',
                  },
                  '& .MuiLineHighlightElement-root': {
                    fill: 'currentColor !important',
                    stroke: 'none !important',
                    strokeWidth: '0 !important',
                  },
                  '& .MuiLineHighlightElement-root circle': {
                    r: `${highlightRadius}px !important`,
                  },
                  '& .MuiMarkElement-root': {
                    fill: 'currentColor !important',
                    stroke: 'currentColor !important',
                    r: '1.75px',
                    strokeWidth: '1px !important',
                    clipPath: 'none !important',
                  },
                  '& .MuiMarkElement-highlighted': {
                    r: '2.25px',
                    strokeWidth: '1px !important',
                    clipPath: 'none !important',
                  },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'flex-end',
                color: resolveSparkStroke,
              }}
            >
              <Box sx={{ width: '100%', borderBottom: '2px solid currentColor' }} />
            </Box>
          )}
        </Box>
      </Box>
    </CloneCellContainer>
  );
};
